import _ from 'lodash';
import React, { useState, useEffect } from 'react';
import {
  useRecoilState,
  useRecoilValue,
  useSetRecoilState
} from 'recoil';
import { useTranslation } from 'react-i18next';
import {
  Drawer,
  Table,
  Button,
  Tooltip,
  notification,
  Avatar
} from 'antd';
import { Comment } from '@ant-design/compatible';
import moment from 'moment';
import { DndContext } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import Grocefy from '../../../../assets/images/grocefyLogoAlone.png';
import {
  setSelectedMasterItemAtom,
  showItemSubstitutesAtom,
  showAddItemSubstituteAtom
} from '../../../../atoms/Atoms';
import api from '../../../../api/api';
import { DraggableBodyRow, DragHandle } from '../../../utils/utils';

function ManageItemSubstitutesDrawer() {
  const selectedMasterItem = useRecoilValue(setSelectedMasterItemAtom);
  const [showItemSubstitutes, setShowItemSubstitutes] =
    useRecoilState(showItemSubstitutesAtom);
  const setShowAddItemSubstitutes =
    useSetRecoilState(showAddItemSubstituteAtom);
  const [loading, setLoading] = useState(false);
  const [substitutes, setSubstitutes] = useState([]);
  const { t } = useTranslation();

  const showMessage = function (message) {
    notification.open({
      message: '',
      description: (
        <Comment
          author={<span>Grocefy</span>}
          avatar={<Avatar src={Grocefy} alt="grocefy" />}
          content={
            <p className="text-sm">
              {message}
            </p>
          }
          datetime={
            <Tooltip title={moment().format('YYYY-MM-DD HH:mm:ss')}>
              <span>{moment().fromNow()}</span>
            </Tooltip>
          }
        />
      ),
    });
  };

  const getSubstitutes = function () {
    setLoading(true);
    api
      .get(`businesses/catalog/substitutes/${selectedMasterItem.id}`)
      .then((response) => {
        setLoading(false);
        if (response.data.success) {
          setSubstitutes(response.data.data.results);
        } else {
          showMessage(response.data.error);
        }
      })
      .catch((error) => {
        setLoading(false);
        showMessage(error);
      });
  };

  const removeClick = function (recommendation) {
    setLoading(true);
    const idToRemove = recommendation.id;
    api
      .post(
        `businesses/catalog/removesubstitutes/${recommendation.id}`,
        { },
      )
      .then((response) => {
        setLoading(false);
        if (response.data.success) {
          const temp = _.filter(substitutes, (r) => r.id !== idToRemove);
          setSubstitutes(temp);
        } else {
          showMessage(response.data.error);
        }
      })
      .catch((error) => {
        setLoading(false);
        showMessage(error);
      });
  };

  const updateRecommendations = function () {
    setLoading(true);
    const tempList = JSON.parse(JSON.stringify(substitutes));
    for (let i = 0; i < tempList.length; i++) {
      api
        .post(
          `businesses/catalog/substitutes/${tempList[i].id}`,
          {
            Ordering: i
          },
        )
        .then((response) => {
          if (response.data.success) {
            if (i === tempList.length - 1) {
              setLoading(false);
            }
          } else {
            showMessage(response.data.error);
          }
        })
        .catch((error) => {
          setLoading(false);
          showMessage(error);
        });
    }
  };

  const onDragEnd = ({ active, over }) => {
    if (active.id !== over?.id) {
      setSubstitutes((prevState) => {
        const activeIndex = prevState.findIndex((record) => record.id === active?.id);
        const overIndex = prevState.findIndex((record) => record.id === over?.id);
        return arrayMove(prevState, activeIndex, overIndex);
      });
    }
  };

  const columns = [
    {
      title: t('sort'),
      dataIndex: 'sort',
      width: 30,
      className: 'drag-visible',
      render: () => <DragHandle />,
    },
    {
      title: t('image'),
      align: 'center',
      className: 'drag-visible',
      render: (row) => (
        <img
          src={row.itemImage}
          alt={`${row.brand} ${row.name} ${row.description}`}
        />
      ),
    },
    {
      title: t('name'),
      align: 'center',
      className: 'drag-visible',
      render: (row) => (
        <span>{row.brand} {row.name} {row.description}</span>
      ),
    },
    {
      title: t('actions'),
      align: 'center',
      className: 'drag-visible',
      render: (row) => (
        <Button
          size="small"
          type="primary"
          danger
          onClick={() => {
            removeClick(row);
          }}
        >
          {t('delete')}
        </Button>
      ),
    }
  ];

  useEffect(() => {
    if (showItemSubstitutes) {
      getSubstitutes();
    }
  }, [showItemSubstitutes]);

  return (
    <Drawer
      title={t('item_substitutes')}
      placement="right"
      closable
      onClose={() => setShowItemSubstitutes(false)}
      open={showItemSubstitutes}
      width={400}
    >
      <>
        <Button
          onClick={() => { setShowAddItemSubstitutes(true); }}
          style={{ marginBottom: 10 }}
        >
          {t('add_new_substitute')}
        </Button>
        <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
          <SortableContext items={substitutes.map((i) => i.id)} strategy={verticalListSortingStrategy}>
            <Table
              size="small"
              bordered
              loading={loading}
              pagination={false}
              dataSource={substitutes}
              columns={columns}
              rowKey="id"
              components={{
                body: {
                  row: DraggableBodyRow,
                },
              }}
            />
          </SortableContext>
        </DndContext>
        <Button
          onClick={() => { updateRecommendations(); }}
          type="primary"
          style={{
            float: 'right',
            marginTop: 10
          }}
        >
          {t('update')}
        </Button>
      </>
    </Drawer>
  );
}

export default ManageItemSubstitutesDrawer;
